import { useState, useRef, useCallback, useEffect } from 'react';
import type { UseOtpInputProps, UseOtpInputReturn } from './types';

export const useOtpInput = ({
  length,
  value,
  onChange,
  onComplete,
  disabled,
}: UseOtpInputProps): UseOtpInputReturn => {
  const [digits, setDigits] = useState<string[]>(() => {
    if (value) {
      const newDigits = value.split('').slice(0, length);
      while (newDigits.length < length) {
        newDigits.push('');
      }
      return newDigits;
    }
    return Array(length).fill('');
  });
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const isEditingRef = useRef(false);

  // Sync with controlled value only when not actively editing
  // or when value is completely different (e.g., reset)
  const prevValueRef = useRef(value);

  useEffect(() => {
    if (
      value !== undefined &&
      !isEditingRef.current &&
      value !== prevValueRef.current
    ) {
      const newDigits = value.split('').slice(0, length);
      while (newDigits.length < length) {
        newDigits.push('');
      }
      setDigits(newDigits);
      prevValueRef.current = value;
    }
  }, [value, length]);

  const updateDigits = useCallback(
    (newDigits: string[]) => {
      isEditingRef.current = true;
      setDigits(newDigits);

      // Send the value without gaps to parent
      const otpValue = newDigits.filter((d) => d !== '').join('');
      onChange?.(otpValue);

      // Keep prevValueRef in sync with what we're sending to parent
      // This ensures proper detection when parent resets the value
      prevValueRef.current = otpValue;

      if (newDigits.every((d) => d !== '') && newDigits.length === length) {
        onComplete?.(otpValue);
      }

      // Reset editing flag after a short delay to allow re-sync
      setTimeout(() => {
        isEditingRef.current = false;
      }, 100);
    },
    [onChange, onComplete, length]
  );

  const handleChange = useCallback(
    (index: number, inputValue: string) => {
      if (disabled) {
        return;
      }

      // Only allow digits
      const digit = inputValue.replace(/\D/g, '').slice(-1);

      const newDigits = [...digits];
      newDigits[index] = digit;
      updateDigits(newDigits);

      // Move to next input if digit entered
      if (digit && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [digits, disabled, length, updateDigits]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) {
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        const newDigits = [...digits];

        if (digits[index]) {
          // Clear current digit
          newDigits[index] = '';
          updateDigits(newDigits);
        } else if (index > 0) {
          // Move to previous and clear
          newDigits[index - 1] = '';
          updateDigits(newDigits);
          inputRefs.current[index - 1]?.focus();
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        inputRefs.current[index - 1]?.focus();
      } else if (e.key === 'ArrowRight' && index < length - 1) {
        e.preventDefault();
        inputRefs.current[index + 1]?.focus();
      }
    },
    [digits, disabled, length, updateDigits]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (disabled) {
        return;
      }

      e.preventDefault();
      const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');
      const pastedDigits = pastedData.split('').slice(0, length);

      const newDigits = [...digits];
      pastedDigits.forEach((digit, i) => {
        if (i < length) {
          newDigits[i] = digit;
        }
      });

      // Fill remaining with empty
      while (newDigits.length < length) {
        newDigits.push('');
      }

      updateDigits(newDigits);

      // Focus last filled or next empty
      const lastFilledIndex = Math.min(pastedDigits.length, length) - 1;
      if (lastFilledIndex >= 0 && lastFilledIndex < length - 1) {
        inputRefs.current[lastFilledIndex + 1]?.focus();
      } else {
        inputRefs.current[lastFilledIndex]?.focus();
      }
    },
    [digits, disabled, length, updateDigits]
  );

  const handleFocus = useCallback((index: number) => {
    setFocusedIndex(index);
  }, []);

  return {
    digits,
    inputRefs,
    handleChange,
    handleKeyDown,
    handlePaste,
    handleFocus,
    focusedIndex,
  };
};
